import { useMemo } from "react";
import { useOrganizationPermissions } from "src/components/permission/use-organization-permissions";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import {
  type SupportAccessType,
  supportAccessTypesRequiringControl,
  UserSupportAccessControlPermission,
} from "src/components/support-access/utils";
import { useUser } from "src/contexts/user-context";

/**
 * Has access to the support access control feature if:
 * 1. Permissions v2 is enabled
 * 2. Either an org admin or a user with the can_grant_support_access permission
 * 3. The org has a support access type that requires control
 * 4. The user is not impersonating
 */
export const useHasSupportAccessControl = (): {
  permission: UserSupportAccessControlPermission | null;
  supportAccessType: SupportAccessType;
  isLoading: boolean;
} => {
  const { user, workspace, isLoading: isUserLoading } = useUser();

  const {
    isPermitted: canGrantSupportAccess,
    isLoading: isResourcePermissionLoading,
  } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_grant_support_access" },
  });

  const { isOrganizationAdmin, isLoading: isOrganizationPermissionsLoading } =
    useOrganizationPermissions();

  return useMemo(() => {
    // support_access_type is a non-null field, but default to most restrictive while loading
    const supportAccessType = workspace?.organization?.support_access_type
      ? (workspace.organization.support_access_type as SupportAccessType)
      : "none";

    const isLoading =
      isUserLoading ||
      isResourcePermissionLoading ||
      isOrganizationPermissionsLoading;

    return {
      permission: getUserSupportAccessControlPermission({
        supportAccessType,
        canGrantSupportAccess,
        isOrganizationAdmin,
        isImpersonating: Boolean(user?.is_impersonating),
      }),
      supportAccessType: supportAccessType,
      isLoading,
    };
  }, [
    workspace?.organization?.support_access_type,
    user?.is_impersonating,
    canGrantSupportAccess,
    isOrganizationAdmin,
    isUserLoading,
    isResourcePermissionLoading,
    isOrganizationPermissionsLoading,
  ]);
};

const getUserSupportAccessControlPermission = (args: {
  supportAccessType: SupportAccessType;
  canGrantSupportAccess: boolean;
  isOrganizationAdmin: boolean;
  isImpersonating: boolean;
}): UserSupportAccessControlPermission | null => {
  const {
    supportAccessType,
    canGrantSupportAccess,
    isOrganizationAdmin,
    isImpersonating,
  } = args;

  const hasValidSupportAccessType =
    supportAccessTypesRequiringControl.includes(supportAccessType);

  // if the support access type does not require control, support access is hidden
  if (!hasValidSupportAccessType) return null;

  // impersonators only get to read
  if (isImpersonating) return UserSupportAccessControlPermission.Readonly;

  // explicit grant and org admins get write access
  return canGrantSupportAccess || isOrganizationAdmin
    ? UserSupportAccessControlPermission.Write
    : UserSupportAccessControlPermission.Readonly;
};
